import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = "/api";

const Shell = ({ children, testId }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6" data-testid={testId}>
    <div className="w-full max-w-md border border-[#1F1F1F] bg-[#0D0D0D] p-10 text-center">{children}</div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "processing" });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "failed" });
      return;
    }
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", order: data });
          return;
        }
      } catch {}
      attempts.current += 1;
      if (attempts.current < 15) setTimeout(poll, 2000);
      else setState({ phase: "timeout" });
    };
    poll();
  }, [sessionId]);

  if (state.phase === "paid") {
    return (
      <Shell testId="payment-success">
        <CheckCircle2 size={40} className="mx-auto text-[#E11D2E]" />
        <h1 className="mt-6 font-display text-2xl font-800 text-white">Order confirmed</h1>
        <p className="mt-3 font-mono text-sm text-[#8A8A8A]" data-testid="order-number">
          {state.order.order_number}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-[#8A8A8A]">
          {state.order.service_name} is queued. Delivery typically starts within 0–6 hours — updates go to your email.
        </p>
        <p className="mt-3 font-mono text-xs text-[#8A8A8A]">
          Track it anytime at <Link to="/track" data-testid="success-track-link" className="text-[#E11D2E] underline underline-offset-4">/track</Link> with your order number and email.
        </p>
        <Link to="/" data-testid="success-home-link" className="mt-8 inline-block bg-[#E11D2E] px-6 py-3 font-mono text-xs uppercase tracking-[0.14em] text-white hover:bg-[#F13B47]">
          Back to site
        </Link>
      </Shell>
    );
  }

  if (state.phase === "failed" || state.phase === "timeout") {
    return (
      <Shell testId="payment-unverified">
        <XCircle size={40} className="mx-auto text-[#8A8A8A]" />
        <h1 className="mt-6 font-display text-2xl font-800 text-white">We couldn't confirm payment yet</h1>
        <p className="mt-4 text-sm text-[#8A8A8A]">If you completed checkout, your order will appear shortly — check your email receipt.</p>
        <Link to="/" data-testid="unverified-home-link" className="mt-8 inline-block border border-[#1F1F1F] px-6 py-3 font-mono text-xs uppercase tracking-[0.14em] text-white hover:border-[#333333]">
          Back to site
        </Link>
      </Shell>
    );
  }

  return (
    <Shell testId="payment-processing">
      <Loader2 size={40} className="mx-auto animate-spin text-[#E11D2E]" />
      <h1 className="mt-6 font-display text-2xl font-800 text-white">Confirming your payment…</h1>
      <p className="mt-4 text-sm text-[#8A8A8A]">This takes a few seconds. Don't close this tab.</p>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testId="payment-cancel">
      <XCircle size={40} className="mx-auto text-[#8A8A8A]" />
      <h1 className="mt-6 font-display text-2xl font-800 text-white">Checkout cancelled</h1>
      <p className="mt-4 text-sm text-[#8A8A8A]">Nothing was charged. Your cart choices are still waiting.</p>
      <Link to="/" data-testid="cancel-home-link" className="mt-8 inline-block bg-[#E11D2E] px-6 py-3 font-mono text-xs uppercase tracking-[0.14em] text-white hover:bg-[#F13B47]">
        Back to catalog
      </Link>
    </Shell>
  );
}
